import { Head, router } from "@inertiajs/react";
import FollowButton from "../../components/FollowButton";
import { TrackEventCard } from "../../components/TrackEventCard";
import { RaceWeekendCard } from "../../components/RaceWeekendCard";
import { Pagination } from "../../components/Pagination";
import { TrackEventFilters } from "../../components/TrackEventFilters";
import { Button } from "@/components/ui/button";
import { useState } from "react";
import type { ProfilePage } from "@/types/generated";
import AuthLayout from "../../layouts/AuthLayout";
import { InertiaPageComponent } from "@/types";
import { toast } from "sonner";

interface ProfileShowProps extends ProfilePage {}

const ProfileShow: InertiaPageComponent<ProfileShowProps> = ({
  user,
  can_follow,
  is_following,
  current_user_is_admin,
  user_has_iracing_account,
  track_events,
  pagination,
  filter_options,
  current_filters,
}) => {
  const [isImporting, setIsImporting] = useState(false);

  const handleAdminImport = () => {
    setIsImporting(true);
    router.post(
      `/iracing/admin_force_import/${user.id}`,
      { limit: 500 },
      {
        onSuccess: (page) => {
          const result = page.props as any;
          if (result.success) {
            toast.success("Import started", {
              description: result.message,
            });
          }
        },
        onError: (errors) => {
          console.error("Admin import error:", errors);
          const errorData = errors as any;

          // Check if this is a reauthorization error
          if (errorData.needs_reauthorization) {
            toast.error("iRacing Refresh Token Expired", {
              description: `User ${errorData.user_email} needs to reconnect their iRacing account. The refresh token expired at: ${errorData.refresh_token_expired_at || 'unknown'}. Please ask the user to visit their profile and reconnect iRacing.`,
            });
          } else {
            toast.error("Failed to start import", {
              description: errorData.error || 'Unknown error',
            });
          }
        },
        onFinish: () => {
          setIsImporting(false);
        },
      }
    );
  };

  return (
    <>
      <Head title={`${user.first_name} ${user.last_name} - Profile`} />

      <div className="min-h-screen bg-gradient-to-br from-gray-100 via-gray-50 to-white">
        <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
          <div className="flex justify-between items-start mb-6">
            <h1 className="text-3xl font-bold text-gray-900 tracking-tight">
              {user.first_name} {user.last_name}'s Profile
            </h1>
            <div className="flex gap-2">
              {can_follow && (
                <FollowButton isFollowing={is_following} userId={user.id} />
              )}
              {current_user_is_admin && user_has_iracing_account && (
                <Button
                  onClick={handleAdminImport}
                  disabled={isImporting}
                  variant="outline"
                  className="border-orange-500 text-orange-600 hover:bg-orange-50"
                >
                  {isImporting ? "Importing..." : "🏁 Force iRacing Import"}
                </Button>
              )}
            </div>
          </div>

          {/* Track Events Section */}
          <div className="space-y-6">
            <div className="mb-6">
              <h2 className="text-2xl font-bold text-gray-900 tracking-tight mb-1">
                Recent Sessions
              </h2>
              <p className="text-gray-600 font-medium">
                Latest track events from {user.first_name}
              </p>
            </div>

            {/* Filters */}
            {filter_options && (
              <TrackEventFilters
                filterOptions={filter_options}
                currentFilters={current_filters || {}}
                userId={user.id.toString()}
              />
            )}

            {track_events && track_events.length > 0 ? (
              <>
                <div className="space-y-4">
                  {track_events.map((item: any, index: number) => {
                    if (item.type === "race_weekend") {
                      return (
                        <RaceWeekendCard
                          key={`race-weekend-${item.iracing_session_id}-${index}`}
                          raceWeekend={item}
                        />
                      );
                    } else {
                      return (
                        <TrackEventCard
                          key={`track-event-${item.id}-${index}`}
                          trackEvent={item}
                        />
                      );
                    }
                  })}
                </div>

                {pagination && pagination.total_pages > 1 && (
                  <div className="mt-8">
                    <Pagination
                      currentPage={pagination.current_page}
                      totalPages={pagination.total_pages}
                      totalCount={pagination.total_count}
                      perPage={pagination.per_page}
                    />
                  </div>
                )}
              </>
            ) : (
              <div className="bg-white shadow rounded-lg p-6 text-center text-gray-500">
                No track events yet.
              </div>
            )}
          </div>
        </main>
      </div>
    </>
  );
};

ProfileShow.layout = (page) => <AuthLayout>{page}</AuthLayout>;

export default ProfileShow;
